import { useState, useEffect } from 'react';
import {
  adminService,
  UserWithConfig,
  UserDeletionStats,
  InboundConfigResponse,
  InboundConfigCreate,
} from '../../../services/adminService';
import type { StatusFilter } from './adminPageTypes';

const ITEMS_PER_PAGE = 10;

export function useAdminPage() {
  const [users, setUsers] = useState<UserWithConfig[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithConfig[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<UserWithConfig | null>(null);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletionStats, setDeletionStats] = useState<UserDeletionStats | null>(null);
  const [deleteConfigTarget, setDeleteConfigTarget] = useState<{ userId: string; userName: string } | null>(null);
  const [deleteConfigLoading, setDeleteConfigLoading] = useState(false);
  const [inboundConfigModal, setInboundConfigModal] = useState<{ userId: string; userName: string } | null>(null);
  const [inboundConfig, setInboundConfig] = useState<InboundConfigResponse | null>(null);
  const [loadingInboundConfig, setLoadingInboundConfig] = useState(false);
  const [inboundEditForm, setInboundEditForm] = useState<InboundConfigCreate>({
    assistant_id: '',
    campaign_context: '',
    is_active: true,
  });
  const [savingInboundConfig, setSavingInboundConfig] = useState(false);
  const [addCreditsTarget, setAddCreditsTarget] = useState<{ userId: string; userName: string } | null>(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsDescription, setAddCreditsDescription] = useState('');
  const [addCreditsLoading, setAddCreditsLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (!successMessage) return;
    const timeout = setTimeout(() => {
      setSuccessMessage(null);
      setDeletionStats(null);
    }, 6000);
    return () => clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await adminService.getUsersWithConfigs();
      setUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
      setSuccessMessage(null);
      setDeletionStats(null);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((user) => {
        if (statusFilter === 'active') return user.assistant_config.exists && user.assistant_config.is_active;
        if (statusFilter === 'inactive') return user.assistant_config.exists && !user.assistant_config.is_active;
        if (statusFilter === 'not-configured') return !user.assistant_config.exists;
        return true;
      });
    }
    setFilteredUsers(filtered);
  };

  const openDeleteConfigModal = (userId: string, userName: string) => {
    setDeleteConfigTarget({ userId, userName });
    setError(null);
  };

  const openInboundConfigModal = async (userId: string, userName: string) => {
    setInboundConfigModal({ userId, userName });
    setInboundConfig(null);
    setError(null);
    setLoadingInboundConfig(true);
    try {
      const config = await adminService.getUserInboundConfig(userId);
      setInboundConfig(config);
      setInboundEditForm({
        assistant_id: config?.assistant_id || '',
        campaign_context: config?.campaign_context || '',
        is_active: config?.is_active ?? true,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load inbound configuration');
    } finally {
      setLoadingInboundConfig(false);
    }
  };

  const handleSaveInboundConfig = async () => {
    if (!inboundConfigModal) return;
    try {
      setSavingInboundConfig(true);
      setError(null);
      await adminService.setUserInboundConfig(inboundConfigModal.userId, inboundEditForm);
      setSuccessMessage('Inbound configuration saved and synced.');
      await openInboundConfigModal(inboundConfigModal.userId, inboundConfigModal.userName);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save inbound configuration');
    } finally {
      setSavingInboundConfig(false);
    }
  };

  const closeInboundConfigModal = () => {
    setInboundConfigModal(null);
    setInboundConfig(null);
  };

  const closeDeleteConfigModal = () => {
    if (deleteConfigLoading) return;
    setDeleteConfigTarget(null);
  };

  const openAddCreditsModal = (userId: string, userName: string) => {
    setAddCreditsTarget({ userId, userName });
    setAddCreditsAmount('');
    setAddCreditsDescription('');
    setError(null);
  };

  const closeAddCreditsModal = () => {
    if (addCreditsLoading) return;
    setAddCreditsTarget(null);
  };

  const handleAddCredits = async () => {
    if (!addCreditsTarget) return;
    const amount = parseInt(addCreditsAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid positive amount');
      return;
    }
    try {
      setAddCreditsLoading(true);
      setError(null);
      const res = await adminService.addCreditsToUser(
        addCreditsTarget.userId,
        amount,
        addCreditsDescription.trim() || undefined
      );
      setSuccessMessage(res.message);
      closeAddCreditsModal();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add credits');
    } finally {
      setAddCreditsLoading(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!deleteConfigTarget) return;
    try {
      setDeleteConfigLoading(true);
      setError(null);
      await adminService.deleteUserAssistantConfig(deleteConfigTarget.userId);
      setSuccessMessage(`Configuration for ${deleteConfigTarget.userName} deleted successfully`);
      closeDeleteConfigModal();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setDeleteConfigLoading(false);
    }
  };

  const openDeleteModal = (user: UserWithConfig) => {
    setDeleteTarget(user);
    setDeleteConfirmValue('');
    setError(null);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
    setDeleteConfirmValue('');
  };

  const handleConfirmDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      setError(null);
      console.log('[useAdminPage.handleConfirmDeleteUser] deleting user=', {
        id: deleteTarget.user.id,
        email: deleteTarget.user.email,
        subscription_plan: deleteTarget.user.subscription_plan,
        subscription_status: deleteTarget.user.subscription_status,
      });
      const response = await adminService.deleteUser(deleteTarget.user.id);
      setSuccessMessage(response.message);
      setDeletionStats(response.stats);
      closeDeleteModal();
      await loadUsers();
    } catch (err: any) {
      console.log('[useAdminPage.handleConfirmDeleteUser] delete failed=', {
        status: err?.response?.status,
        data: err?.response?.data,
      });
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const canConfirmDeletion =
    deleteTarget && deleteConfirmValue.trim().toLowerCase() === deleteTarget.user.email.toLowerCase();

  const formatStatLabel = (key: keyof UserDeletionStats) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const getStatusColor = (user: UserWithConfig) => {
    if (!user.assistant_config.exists) return 'bg-red-500/20 text-red-300 border border-red-500/30';
    if (!user.assistant_config.is_active) return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
    return 'bg-green-500/20 text-green-300 border border-green-500/30';
  };

  const getStatusText = (user: UserWithConfig) => {
    if (!user.assistant_config.exists) return 'Not Configured';
    if (!user.assistant_config.is_active) return 'Inactive';
    return 'Active';
  };

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  return {
    users,
    filteredUsers,
    paginatedUsers,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,
    loading,
    error,
    successMessage,
    setSuccessMessage,
    deletionStats,
    setDeletionStats,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    deleteTarget,
    deleteConfirmValue,
    setDeleteConfirmValue,
    deleteLoading,
    deleteConfigTarget,
    deleteConfigLoading,
    inboundConfigModal,
    inboundConfig,
    loadingInboundConfig,
    inboundEditForm,
    setInboundEditForm,
    savingInboundConfig,
    addCreditsTarget,
    addCreditsAmount,
    setAddCreditsAmount,
    addCreditsDescription,
    setAddCreditsDescription,
    addCreditsLoading,
    loadUsers,
    openDeleteConfigModal,
    openInboundConfigModal,
    handleSaveInboundConfig,
    closeInboundConfigModal,
    closeDeleteConfigModal,
    openAddCreditsModal,
    closeAddCreditsModal,
    handleAddCredits,
    handleDeleteConfig,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDeleteUser,
    canConfirmDeletion,
    formatStatLabel,
    getStatusColor,
    getStatusText,
  };
}
